// Credit usage for the signed-in user — a DEVELOPMENT surface for /usage.
//
// This is not the admin console the pricing deck describes (slide 12: allocate credits, approve
// top-ups, set caps, export the ledger). That is Phase 2 and needs an admin role this build has no
// concept of. What this page does is make Phase 1 auditable by eye: it shows what the ledger, the
// wallet and the period actually hold for ONE user, so a number on screen can be checked against
// the database instead of taken on trust.
//
// Ant Design + the admin shell — env comes from the global selector, the tenant from a per-page one,
// because the sandbox login is pinned to neither.
//
// ⚠ IT SHOWS NO SUPPLIER COST, and that is deliberate rather than missing. The deck's customer-trust
//   policy (slide 12) is to "show credit deduction details per conversation without exposing OpenAI
//   supplier costs", so the endpoint never selects `actual_openai_cost` and there is nothing here to
//   render. `credits` and `amountGbp` are the customer-facing pair.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Progress, Row, Select, Space, Statistic, Table, Tag, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { tenantApi, usageApi } from "../../services/api";
import { useEnvironment } from "../../context/EnvironmentContext";

const gbp = (v) => `£${Number(v || 0).toFixed(4)}`;
const num = (v) => Number(v || 0).toLocaleString();
// Credits carry two decimals since billing phase 4, and are always shown with both.
const credits = (v) => Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const day = (v) => (v ? new Date(v).toISOString().slice(0, 10) : "—");
const stamp = (v) => (v ? new Date(v).toISOString().replace("T", " ").slice(0, 19) : "—");

export default function UsagePage() {
  const { selectedEnv, changeEnv, envOptions } = useEnvironment();
  const [tenants, setTenants] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setTenants([]);
    setTenant(null);
    setData(null);
    tenantApi
      .getAllForEnv(selectedEnv)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data.tenants || [];
        setTenants(list);
        // Preselect so the page shows something on arrival rather than an empty frame.
        const first = list[0] && (list[0].name || list[0].schema || list[0]);
        if (first) setTenant(first);
      })
      .catch((err) => {
        if (!cancelled) message.error(err.response?.data?.error || "Could not load tenants");
      });
    return () => { cancelled = true; };
  }, [selectedEnv]);

  const load = useCallback(() => {
    if (!tenant) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    usageApi
      .get(selectedEnv, tenant)
      .then((res) => { if (!cancelled) { setData(res.data); setLoading(false); } })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || err.message || "Could not load usage");
        setData(null);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedEnv, tenant]);

  useEffect(() => load(), [load]);

  const tenantOptions = useMemo(
    () => tenants.map((t) => {
      const name = t.name || t.schema || t;
      return { value: name, label: name };
    }),
    [tenants]
  );

  const wallet = data && data.wallet;
  const credit = data && data.credit;
  const period = data && data.period;
  const plan = data && data.plan;

  const columns = [
    {
      title: "Turn / call",
      dataIndex: "transactionId",
      render: (v) => {
        // "<turnId>" is one row for the whole turn; "<turnId>:<callIndex>" one model call, in the shape before it.
        const [turn, call] = String(v).split(":");
        return <span className="font-mono text-xs">{turn.slice(0, 8)}…<Tag className="ml-2">{call === undefined ? "turn" : `call ${call}`}</Tag></span>;
      },
    },
    { title: "Type", dataIndex: "type", render: (v) => <Tag color={v === "OVERAGE" ? "orange" : "blue"}>{v}</Tag> },
    { title: "Model", dataIndex: "model" },
    {
      // Fresh and cached are shown apart because they bill an order of magnitude apart; one
      // "input" figure would hide the whole reason caching is worth anything.
      title: "Tokens (in / cached / out)",
      key: "tokens",
      render: (_, r) => (
        <span className="text-xs">
          {num(r.inputTokens)} / {num(r.cachedTokens)} / {num(r.outputTokens)}
        </span>
      ),
    },
    { title: "Credits", dataIndex: "credits", align: "right", render: (v) => credits(v) },
    { title: "Charge", dataIndex: "amountGbp", align: "right", render: (v) => gbp(v) },
    { title: "When (UTC)", dataIndex: "createdOn", render: (v) => <span className="text-xs">{stamp(v)}</span> },
  ];

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-semibold m-0">My AI usage</h1>
          <p className="text-sm opacity-60 m-0">
            Credits this sign-in has spent in the open billing period. Development view — not the admin console.
          </p>
        </div>
        <Space wrap>
          <Select
            value={selectedEnv}
            onChange={changeEnv}
            options={(envOptions || []).map((e) => ({ value: e, label: e }))}
            style={{ width: 110 }}
          />
          <Select
            value={tenant}
            onChange={setTenant}
            options={tenantOptions}
            placeholder="tenant"
            showSearch
            style={{ width: 220 }}
          />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </Space>
      </div>

      {error && <Alert type="error" showIcon message={error} className="mb-4" />}

      {data && !data.enrolled && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="This tenant has no open billing period"
          description={
            "This page reads a month's usage off its period, so it has nothing to show. " +
            "Open one with scripts/seed-ai-subscription.js, then seed the wallet with scripts/seed-ai-wallet.js."
          }
        />
      )}

      {data && data.enrolled && (
        <>
          <Row gutter={[16, 16]} className="mb-4">
            <Col xs={12} md={6}>
              <Card size="small"><Statistic title="Credits used" value={credits(data.user.credits)} /></Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small"><Statistic title="Charged" value={gbp(data.user.amountGbp)} /></Card>
            </Col>
            <Col xs={12} md={6}>
              {/* Questions, not calls — one question is several model calls, and only the first
                  number means anything to the person who asked them. */}
              <Card size="small">
                <Statistic title="Questions asked" value={data.user.turns} suffix={<span className="text-xs opacity-60">in {num(data.user.conversations)} chats</span>} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Model calls" value={data.user.calls} suffix={<span className="text-xs opacity-60">{num(data.user.tokens)} tokens</span>} />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} className="mb-4">
            <Col xs={24} lg={8}>
              {/* The tenant's wallet, in the figures the gate compares a turn against (server/utils/aiHold.js). The middle
                  line goes when nothing is in use. */}
              <Card size="small" title="Credits">
                {credit && credit.live ? (
                  <div className="text-sm">
                    <div className="flex justify-between"><span>Your credits</span><strong>{credits(credit.balance)}</strong></div>
                    {credit.inUse !== 0 && (
                      <div className="flex justify-between"><span>Currently in use</span><strong>{credits(credit.inUse)}</strong></div>
                    )}
                    <div className="flex justify-between border-t mt-1 pt-1"><span>Available</span><strong>{credits(credit.available)}</strong></div>
                    {credit.available < 0 && <Tag color="red" className="mt-2">below zero — new questions are refused</Tag>}
                  </div>
                ) : (
                  <Alert
                    type="warning"
                    showIcon
                    message="Wallet not live"
                    description="This tenant's turns are not metered until its wallet is seeded: scripts/seed-ai-wallet.js."
                  />
                )}
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card size="small" title="My wallet">
                {wallet ? (
                  <>
                    <Progress
                      percent={wallet.allocatedCredits ? Math.min(100, Math.round((wallet.consumedCredits / wallet.allocatedCredits) * 100)) : 0}
                      status={wallet.availableCredits <= 0 ? "exception" : "normal"}
                    />
                    <div className="text-sm mt-2">
                      <div>Allocated <strong>{num(wallet.allocatedCredits)}</strong></div>
                      <div>Used <strong>{num(wallet.consumedCredits)}</strong></div>
                      <div>Left <strong>{num(wallet.availableCredits)}</strong></div>
                      {!wallet.aiEnabled && <Tag color="red" className="mt-2">AI disabled for this user</Tag>}
                    </div>
                  </>
                ) : (
                  // Absent row is a real state with real consequences, so it is described rather
                  // than rendered as zeros.
                  <Alert
                    type="info"
                    showIcon
                    message="No wallet allocated"
                    description="Turns draw straight from the tenant's credits. Per-user allocation is Phase 2."
                  />
                )}
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card size="small" title="Plan & rate">
                {plan ? (
                  <div className="text-sm">
                    <div><strong>{plan.name}</strong> <Tag>{plan.billingFrequency}</Tag></div>
                    <div>£{num(plan.monthlyPriceGbp)}/month · {num(plan.includedCredits)} credits included</div>
                    {data.rate && (
                      <div className="opacity-70 mt-1">
                        {gbp(data.rate.standardCreditPriceGbp)} a credit
                      </div>
                    )}
                    <div className="mt-2">
                      Period {day(period.start)} → {day(period.end)} <Tag color="green">{period.status}</Tag>
                    </div>
                  </div>
                ) : (
                  <Alert type="info" showIcon message="No subscription found for this period" />
                )}
              </Card>
            </Col>
          </Row>

          {data.byDay.length > 0 && (
            <Card size="small" title="By day (UTC)" className="mb-4">
              <Space direction="vertical" className="w-full">
                {data.byDay.map((d) => {
                  const max = Math.max(...data.byDay.map((x) => x.credits)) || 1;
                  return (
                    <div key={day(d.day)} className="flex items-center gap-3">
                      <span className="text-xs font-mono" style={{ width: 90 }}>{day(d.day)}</span>
                      <Progress
                        percent={Math.round((d.credits / max) * 100)}
                        showInfo={false}
                        className="flex-1 m-0"
                      />
                      <span className="text-xs" style={{ width: 150 }}>
                        {credits(d.credits)} credits · {gbp(d.amountGbp)} · {num(d.calls)} calls
                      </span>
                    </div>
                  );
                })}
              </Space>
            </Card>
          )}

          <Card
            size="small"
            title="Recent charges"
            extra={<span className="text-xs opacity-60">one row per movement — a question's charge, and its hold and release — {num(data.recent.length)} most recent</span>}
          >
            <Table
              size="small"
              rowKey="transactionId"
              dataSource={data.recent}
              columns={columns}
              loading={loading}
              pagination={false}
              scroll={{ x: true }}
              locale={{ emptyText: "No charges in this period yet — ask something on the chat page." }}
            />
          </Card>
        </>
      )}
    </div>
  );
}
